import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  PointOfSale,
  Receipt,
  TableRestaurant,
  LocalOffer,
  Fullscreen,
  FullscreenExit,
  AddCircleOutline,
  Home,
} from "@mui/icons-material";

const POSHeader = ({
  mainTab,
  setMainTab,
  isFullscreen,
  toggleFullscreen,
  fullscreenLabel,
  onNewOrder,
  onTablesClick,
  onOffersClick,
  onPOSClick,
  onHomeClick,
  showNewOrderButton = false,
}) => {
  const theme = useTheme();

  // Navigation button style generator - Modern Layout Style
  const getNavButtonStyles = (isActive) => ({
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "8px",
    px: 2,
    py: 1,
    fontSize: "0.875rem",
    minWidth: 110,
    color: isActive ? "#ffffff" : theme.palette.text.primary,
    background: isActive
      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
      : "transparent",
    border: "none",
    transition: "all 0.2s ease",
    boxShadow: isActive
      ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
      : "none",

    "&:hover": {
      background: isActive
        ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
        : alpha(theme.palette.primary.main, 0.08),
      transform: "translateY(-2px)",
      boxShadow: isActive
        ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
        : `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
    },

    "&:active": {
      transform: "translateY(-1px)",
    },
  });

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${theme.palette.background.paper} 100%)`,
        backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.02)}`,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          minHeight: 72,
        }}
      >
        {/* Left: Branding Section */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 180 }}
        >
          <Box
            component="img"
            src="/assets/logo.png"
            alt="Menux"
            sx={{
              height: { xs: 26, sm: 30 },
              width: "auto",
              objectFit: "contain",
            }}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                lineHeight: 1.2,
                color: theme.palette.text.primary,
                letterSpacing: "-0.2px",
              }}
            >
              POS Operations
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.7),
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              Manage orders & transactions
            </Typography>
          </Box>
        </Box>

        {/* Center: Navigation Tabs */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            p: 0.75,
            borderRadius: "12px",
            background: alpha(
              theme.palette.common.black,
              theme.palette.mode === "dark" ? 0.2 : 0.06,
            ),
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          }}
        >
          {onHomeClick && (
            <Button
              startIcon={<Home sx={{ fontSize: 20 }} />}
              onClick={onHomeClick}
              sx={getNavButtonStyles(false)}
            >
              Home
            </Button>
          )}
          <Button
            startIcon={<PointOfSale sx={{ fontSize: 20 }} />}
            onClick={() => {
              setMainTab(0);
              if (onPOSClick) onPOSClick();
            }}
            sx={getNavButtonStyles(mainTab === 0)}
          >
            POS Dashboard
          </Button>
          <Button
            startIcon={<Receipt sx={{ fontSize: 20 }} />}
            onClick={() => setMainTab(1)}
            sx={getNavButtonStyles(mainTab === 1)}
          >
            Orders
          </Button>
          <Button
            startIcon={<TableRestaurant sx={{ fontSize: 20 }} />}
            onClick={onTablesClick}
            sx={getNavButtonStyles(mainTab === 2)}
          >
            Tables
          </Button>
          <Button
            startIcon={<LocalOffer sx={{ fontSize: 20 }} />}
            onClick={onOffersClick}
            sx={getNavButtonStyles(mainTab === 3)}
          >
            Offers
          </Button>
        </Box>

        {/* Right: Action Buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 180,
            justifyContent: "flex-end",
          }}
        >
          <Tooltip
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            arrow
          >
            <IconButton
              onClick={toggleFullscreen}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                color: alpha(theme.palette.text.secondary, 0.7),
                transition: "all 0.25s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  transform: "translateY(-1px)",
                },
              }}
            >
              {isFullscreen ? (
                <FullscreenExit fontSize="small" />
              ) : (
                <Fullscreen fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          {showNewOrderButton && (
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={onNewOrder}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 2.5,
                py: 1.25,
                fontSize: "0.9rem",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                transition: "all 0.25s ease",
                "&:hover": {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
              }}
            >
              New Order
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default POSHeader;
